import React, { useState } from 'react'
import PropTypes from 'prop-types'

import { ErrorBoundary } from 'react-error-boundary'
import { useQuery } from '@tanstack/react-query'

import { getAkDataFromSessionStorage, getWording } from './utils'
import { OrderApiContextProvider, createOrderApiClient } from './OrderApi'
import { CrmApiContextProvider, createCrmApiClient } from './CrmApi'

import useTrackingContext from './hooks/useTrackingContext'

import CatalogForm from './components/CatalogForm'
import Upsell from './components/Upsell'
import ErrorPage from './components/ErrorPage'
import Header from './components/Header'
import Thanks from './components/Thanks'
import { Button } from 'react-bootstrap'
import { ORGANISATIONS_PER_UPSELL_ROUND } from './constants'

/*
   NOTE: When testing Thanks page, uncomment this to load a fixture with organisations
   without API request. Also set:

   const [thanks, setThanks] = useState(true)
   // and:
   const [orderedOrganisations, setOrderedOrganisations] = useState(schueleraustauschOrganisations)
 */
// import schueleraustauschOrganisations from './fixtures/schueleraustausch_organisations.json'

const shuffleArray = (array) => {

  for (let i = array.length - 1; i > 0; i--) {
    let j = Math.floor(Math.random() * (i + 1));      // random index
    [ array[i], array[j] ] = [ array[j], array[i] ];  // swap
  }

  return array
}

const CatalogOrderRoot = (props) => {
  const orderApiClient = createOrderApiClient({ apiUrl: props.apiUrl, apiToken: props.apiToken })
  const crmApiClient = createCrmApiClient({ apiUrl: props.crmApiUrl, apiToken: props.crmApiToken })
  const organisationSorter = props.organisationSorter || shuffleArray

  const tracker = useTrackingContext()
  const { forwarded, entrypoint } = getAkDataFromSessionStorage()
  const isBrowser = typeof window !== 'undefined'

  let referrer

  if (isBrowser) {
    referrer = props.referrer || document.location.href
  } else {
    referrer = props.referrer
  }

  const businessArea = props.businessArea
  const interestInSchoolPrograms = props.interestInSchoolPrograms === true
  const programId = props.programId

  // Schueleraustausch specific
  const exchangeCountryCode = (props.exchangeCountryCode || '').toUpperCase()

  // ensure these are functions, to avoid conditionals before calling them
  const afterSubmit = props.afterSubmit || function() {}
  const onReady = props.onReady || function() {}
  const onFinish = props.onFinish || function() {}

  const [thanks, setThanks] = useState(false)
  // const [thanks, setThanks] = useState(true)


  /* set this to e.g.
     `{ target_group: 'adults' }` or `{ exchange_country_code: 'US' }`
     to preview sprachreisen or schueleraustausch upsell locally
     without needing to submit an order!
   */
  const [initialOrder, setInitialOrder] = useState()


  /*
     Number of upsell rounds we will see. Initially none, but after first
     order, we will know how many there will be.
     We need this to display progress bar i.e. also on "Thanks".
   */
  const [upsellRounds, setUpsellRounds] = useState(0)

  /*
     Track how many catalogs have been ordered via upsell,
     to be able to stop passing phone numbers when limit is
     reached. (Only relevant for schueleraustausch)
   */
  const [upsellCount, setUpsellCount] = useState(0)

  /*
     Track which organisations-catalogs were ordered to be able
     to display Catalog PDFs links on thanks page.
     This includes the initial order as well as all selected upsells.
   */
  const [orderedOrganisations, setOrderedOrganisations] = useState([])
  // const [orderedOrganisations, setOrderedOrganisations] = useState(schueleraustauschOrganisations)

  const {
    isLoading,
    isError,
    error,
    data: siteAreaOrganisations
  } = useQuery({
    queryKey: ['site', 'area', businessArea, 'organisations'],
    queryFn: () => {
      return crmApiClient.fetchSiteAreaOrganisations(businessArea)
    },
  })

  const onErrorPageClick = () => {
    tracker.trackEvent('CatalogOrder', 'ErrorPage button click')
    onFinish()
  }

  const onFormCancel = () => {
    tracker.trackEvent('CatalogOrder', 'Form canceled')
    if (initialOrder) {
      setThanks(true)
    } else {
      onFinish()
    }
  }

  const handleOrderCreated = (order) => {
    afterSubmit(order)
    setInitialOrder(order)
    const orderedOrganisation = siteAreaOrganisations.find(({slug}) => order.organisation_slug === slug)
    setOrderedOrganisations([orderedOrganisation])
  }

  const handleUpsellRoundFinished = ({ orderedOrganisations: ordered, orderedOrganisationCount }) => {
    setUpsellCount(orderedOrganisationCount)

    // Add newly ordered upsell orgas to already ordered.
    setOrderedOrganisations([...orderedOrganisations, ...ordered])

    afterSubmit()
  }

  const handleUpsellFinished = () => {
    setThanks(true)
  }

  if (isLoading) {
    return (
      <p className='text text-warning'>Formular wird geladen...</p>
    )
  }

  if (isError) {
    return (
      <ErrorPage onExit={onErrorPageClick} error={error} />
    )
  }

  const organisation = siteAreaOrganisations.find(({ slug }) => slug === props.organisationId)

  if (!organisation) {
    return (
      <ErrorPage onExit={onErrorPageClick} error={{
        message: `Organisation '${props.organisationId}' wurde nicht gefunden.`
      }} />
    )
  }

  const wording = getWording(organisation.name, businessArea, {
    headline: props.headline,
    subline: props.subline,
    teaser: props.teaser,
  })

  const willShowUpsell = organisation.upsell_after && props.upsell

  const upsellableOrganisations = siteAreaOrganisations.filter((orga) => {
    return (orga.show_in_upsell === true && orga.catalog_enabled === true)
  })

  let renderable

  if (thanks) {
    renderable = (
      <Thanks
        businessArea={businessArea}
        targetGroup={initialOrder.target_group}
        organisations={orderedOrganisations}
        upsellRounds={upsellRounds}
      >
        {businessArea === 'schueleraustausch'
        ? (
          <>
            <p>
              Und wie versprochen ist hier dein Notion Planer:
            </p>

          <div className='hstack gap-3'>
            <Button
              as='a' target='_blank' variant='primary'
              href={'https://learned-square-523.notion.site/mein-Auslandsjahr-1984c96eaf0380d5be54c8233ef6ec4d'}
            >
              Notion Planer laden
            </Button>
            <Button variant='outline-primary' onClick={onFinish}>Zurück zur Webseite</Button>
          </div>
          </>
        )
        : (
          <Button onClick={onFinish}>Zurück zur Webseite</Button>
        )}


      </Thanks>
    )
  } else {
    if (!willShowUpsell && initialOrder) {
      // we're done, so just finish now
      tracker.trackEvent('CatalogOrder', 'Process complete')

      setThanks(true)
    }

    if (willShowUpsell && initialOrder) {
      tracker.trackEvent('CatalogOrder', 'Upsellable organisations: ' + upsellableOrganisations.length)
    }


    if (initialOrder) {
      {/* FIXME:  this does not work as expected, shuffling will be
          reverted within Upsell as organisations are sorted by slug ascending
          after prioritizing Placements
        */}
      renderable = (
        <Upsell
          businessArea={businessArea}
          interestInSchoolPrograms={interestInSchoolPrograms}
          organisations={organisationSorter(upsellableOrganisations)}  // lets shuffle these orgas, to prevent organisations to be always on last place
          initialOrder={initialOrder}
          onFinish={handleUpsellFinished /* will be called once after upsell is finished */}
          afterSubmit={handleUpsellRoundFinished /* will be called after every upsell round */}
          prechecked={props.upsellPrechecked}
          upsellCount={upsellCount}
          upsellRounds={upsellRounds}
          setUpsellRounds={setUpsellRounds}
        />
      )
    } else {
      onReady()

      renderable = (
        <ErrorBoundary FallbackComponent={ErrorPage}>
          <CatalogForm
            organisation={organisation}
            businessArea={businessArea}
            exchangeCountryCode={exchangeCountryCode}
            interestInSchoolPrograms={interestInSchoolPrograms}
            programId={programId}
            referrer={referrer}
            forwarded={forwarded}
            entrypoint={entrypoint}
            onSuccess={handleOrderCreated}
            onCancel={onFormCancel}
          >
            <Header
              wording={wording}
              logoUrl={organisation.logo_url}
              organisationName={organisation.shortname}
            />
          </CatalogForm>
        </ErrorBoundary>
      )
    }
  }

  return (
    <CrmApiContextProvider client={crmApiClient}>
      <OrderApiContextProvider client={orderApiClient}>
        <div className='ak-catalog-order'>
          {renderable}
        </div>
      </OrderApiContextProvider>
    </CrmApiContextProvider>
  )
}

CatalogOrderRoot.propTypes = {
  businessArea: PropTypes.oneOf(['sprachreisen', 'schueleraustausch']).isRequired,
  apiUrl: PropTypes.string.isRequired,
  apiToken: PropTypes.string.isRequired,
  crmApiUrl: PropTypes.string.isRequired,
  crmApiToken: PropTypes.string.isRequired,
  organisationId: PropTypes.string.isRequired,
  exchangeCountryCode: PropTypes.string,
  organisationSorter: PropTypes.func,
  interestInSchoolPrograms: PropTypes.bool,
  programId: PropTypes.string,
  referrer: PropTypes.string,
  headline: PropTypes.string,
  subline: PropTypes.string,
  teaser: PropTypes.string,
  upsell: PropTypes.bool.isRequired,
  upsellPrechecked: PropTypes.bool.isRequired,
  onFinish: PropTypes.func.isRequired,
  onReady: PropTypes.func,
  afterSubmit: PropTypes.func,
}

export default CatalogOrderRoot
